import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { SelectItem } from 'primeng/api';
import { forkJoin, of, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { EmployeeServices } from '../../employees/services/employee.services';
import {
  Employee,
  BusinessDepartment,
  EmployeeType,
  EmployeePosition
} from '../../employees/models/employee';
import { UserService } from '../../users/services/user.service';
import { SaveUsers } from '../../users/models/user';
import { BusinessDepartmentService } from '../../business-department.services';
import { BaseServices } from '../../kuba.services';
import { FeatureKey, Roles } from '../../../_models/feature';
import { PasswordValidatorModule } from '../../../shared/password-validator/password-validator.module';
import { NotificationService } from '../../../shared/services/notification.service';

/** The dropdowns in this dialog that can be added to without leaving it. */
type AddableOption = 'type' | 'position' | 'department';

@Component({
  selector: 'business-settings-person-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    DialogModule,
    SelectModule,
    MultiSelectModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    PasswordValidatorModule
  ],
  providers: [EmployeeServices, UserService, BusinessDepartmentService],
  templateUrl: './business-settings-person-dialog.component.html',
  styleUrls: ['./business-settings-person-dialog.component.css']
})
export class BusinessSettingsPersonDialogComponent
  implements OnInit, OnDestroy
{
  @Input() businessId: number;
  @Input() applicationId: number;
  @Input() businessName = '';
  @Input() withLogin = false;
  @Output() closed = new EventEmitter<void>();
  @Output() changed = new EventEmitter<void>();
  private readonly fb = inject(FormBuilder);
  private readonly employees = inject(EmployeeServices);
  private readonly users = inject(UserService);
  private readonly departmentsService = inject(BusinessDepartmentService);
  private readonly notifications = inject(NotificationService);
  private readonly destroy$ = new Subject<void>();
  readonly form = this.fb.group({
    name: ['', Validators.required],
    email: ['', Validators.email],
    type: [null as number | null, Validators.required],
    position: [null as number | null, Validators.required],
    departments: [[] as number[], Validators.required],
    createLogin: [false],
    username: [''],
    role: [Roles.USER]
  });
  types: SelectItem[] = [];
  positions: SelectItem[] = [];
  departments: BusinessDepartment[] = [];
  readonly roles = [
    { label: 'USER_ROLE_DROPDOWN.USER', value: Roles.USER },
    { label: 'USER_ROLE_DROPDOWN.EDITOR', value: Roles.EDITOR }
  ];
  loading = true;
  saving = false;
  errorKey = '';

  /* ─── Inline "add an option" ─── */

  /**
   * Which dropdown is currently showing its add row, if any.
   *
   * Inline rather than a nested dialog: this dialog is already the thing the
   * user is in the middle of, and stacking a second modal over it to type one
   * word costs more than it saves. Only one is open at a time.
   */
  adding: AddableOption | null = null;
  newOptionName = '';
  addingSaving = false;
  addErrorKey = '';
  createdEmployeeId = 0;
  password = '';
  passwordValid = false;

  ngOnInit(): void {
    this.form.controls.createLogin.setValue(this.withLogin);
    this.form.controls.createLogin.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateLoginValidation());
    this.updateLoginValidation();
    this.loadOptions();
  }

  private updateLoginValidation(): void {
    const required = this.form.controls.createLogin.value;
    this.form.controls.email.setValidators(
      required ? [Validators.required, Validators.email] : [Validators.email]
    );
    this.form.controls.username.setValidators(
      required ? [Validators.required] : []
    );
    this.form.controls.email.updateValueAndValidity();
    this.form.controls.username.updateValueAndValidity();
  }

  /** Opens the add row under a dropdown, or closes it if it is already open. */
  startAdd(kind: AddableOption): void {
    if (this.saving || this.loading) return;
    const reopening = this.adding !== kind;
    this.cancelAdd();
    if (reopening) this.adding = kind;
  }

  cancelAdd(): void {
    this.adding = null;
    this.newOptionName = '';
    this.addErrorKey = '';
  }

  /** Label for the add control, per dropdown. */
  addLabelKey(kind: AddableOption): string {
    if (kind === 'type') return 'BUSINESS_SETTINGS.ADD_TYPE';
    if (kind === 'position') return 'BUSINESS_SETTINGS.ADD_POSITION';
    return 'BUSINESS_SETTINGS.ADD_DEPARTMENT';
  }

  saveNewOption(): void {
    const kind = this.adding;
    const name = this.newOptionName.trim();
    if (!kind || !name || this.addingSaving) return;
    this.addingSaving = true;
    this.addErrorKey = '';
    this.createOption(kind, name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: created => {
          this.addingSaving = false;
          // A name that already exists comes back as an empty body with a 200,
          // not as an error — the same convention the employee form and
          // kuba-control both read these endpoints by.
          if (!(created?.Id > 0)) {
            this.addErrorKey = 'BUSINESS_SETTINGS.OPTION_NAME_EXISTS';
            return;
          }
          this.applyCreatedOption(kind, created);
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.cancelAdd();
        },
        error: () => {
          this.addingSaving = false;
          this.addErrorKey = 'BUSINESS_SETTINGS.ADD_OPTION_FAILED';
        }
      });
  }

  private createOption(kind: AddableOption, name: string) {
    const common = {
      Id: 0,
      BusinessId: this.businessId,
      ApplicationId: this.applicationId,
      Status: '1',
      CreatedBy: BaseServices.UserId
    };
    if (kind === 'type') {
      return this.employees.createType(
        Object.assign(new EmployeeType(), common, { Type: name })
      );
    }
    if (kind === 'position') {
      return this.employees.createPosition(
        Object.assign(new EmployeePosition(), common, { Name: name })
      );
    }
    return this.departmentsService.addDepartment({ ...common, Name: name });
  }

  /**
   * Puts the new option in its dropdown and selects it, rather than re-fetching
   * every list — the user added it to use it, and a reload would also discard
   * whatever else they have already typed into the form.
   */
  private applyCreatedOption(kind: AddableOption, created: any): void {
    if (kind === 'type') {
      this.types = [...this.types, { label: created.Type, value: created.Id }];
      this.form.controls.type.setValue(created.Id);
      return;
    }
    if (kind === 'position') {
      this.positions = [
        ...this.positions,
        { label: created.Name, value: created.Id }
      ];
      this.form.controls.position.setValue(created.Id);
      return;
    }
    this.departments = [
      ...this.departments,
      { Id: created.Id, Name: created.Name } as BusinessDepartment
    ];
    // Department is a multi-select, so the new one joins the selection instead
    // of replacing what is already ticked.
    this.form.controls.departments.setValue([
      ...(this.form.controls.departments.value ?? []),
      created.Id
    ]);
  }

  loadOptions(): void {
    if (String(BaseServices.AbsoluteroleId) !== '1' || !(this.businessId > 0))
      return;
    this.loading = true;
    this.errorKey = '';
    forkJoin({
      types: this.employees.getAllByType(this.businessId),
      positions: this.employees.getAllByPosition(this.businessId),
      departments: this.departmentsService.getDepartmentDropdownForBusiness(
        this.businessId,
        true
      )
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.types = result.types.map(type => ({
            label: type.Type,
            value: type.Id
          }));
          this.positions = result.positions.map(position => ({
            label: position.Name,
            value: position.Id
          }));
          this.departments = result.departments;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.errorKey = 'BUSINESS_SETTINGS.LOAD_PEOPLE_FAILED';
        }
      });
  }

  save(): void {
    if (
      String(BaseServices.AbsoluteroleId) !== '1' ||
      !(this.businessId > 0) ||
      !(this.applicationId > 0) ||
      this.saving ||
      this.loading ||
      this.form.invalid ||
      (this.form.controls.createLogin.value && !this.passwordValid)
    )
      return;
    this.saving = true;
    this.errorKey = '';
    const values = this.form.getRawValue();
    const employee = Object.assign(new Employee(), {
      Id: 0,
      BusinessId: this.businessId,
      ApplicationId: this.applicationId,
      Name: values.name,
      Email: values.email,
      TypeOfEmployeeId: values.type,
      EmployeePositionId: values.position,
      BusinessDepartmentIds: values.departments,
      BusinessDepartmentId: 0,
      Status: '1',
      CreatedBy: BaseServices.UserId,
      CountryCode: 2,
      EmployeeDocument: [],
      IsEmail: false,
      IsSms: false
    });
    const employee$ =
      this.createdEmployeeId > 0
        ? of({ Id: this.createdEmployeeId })
        : this.employees.add(employee);
    employee$
      .pipe(
        switchMap((saved: Employee) => {
          if (!(saved?.Id > 0))
            throw new Error('Employee creation returned no ID.');
          this.createdEmployeeId = saved.Id;
          // A failed login request can be retried without creating a duplicate employee.
          for (const key of [
            'name',
            'type',
            'position',
            'departments',
            'createLogin'
          ]) {
            this.form.get(key).disable();
          }
          if (!values.createLogin) return of(saved);
          const user = Object.assign(new SaveUsers(), {
            Id: 0,
            BusinessId: this.businessId,
            ApplicationId: this.applicationId,
            FeatureId: FeatureKey.EMPLOYEES,
            FeatureKey: saved.Id,
            Name: values.name,
            Email: values.email,
            Username: values.username,
            Password: this.password,
            RoleId: values.role,
            Status: '1',
            LanguageId: BaseServices.userLanguageId,
            CreatedBy: BaseServices.UserId,
            Rights: null,
            SendUserData: false
          });
          return this.users.addusers(user).pipe(
            map(result => {
              if (!((result as unknown as SaveUsers)?.Id > 0))
                throw new Error('User creation returned no ID.');
              return result;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.password = '';
          this.users.clearCachedUserList();
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.changed.emit();
          this.closed.emit();
        },
        error: () => {
          this.saving = false;
          this.errorKey =
            this.createdEmployeeId > 0
              ? 'BUSINESS_SETTINGS.LOGIN_CREATE_FAILED'
              : 'BUSINESS_SETTINGS.EMPLOYEE_CREATE_FAILED';
          if (this.createdEmployeeId > 0) this.changed.emit();
        }
      });
  }

  ngOnDestroy(): void {
    this.password = '';
    this.destroy$.next();
    this.destroy$.complete();
  }
}
